import { DateTime } from 'luxon';
import { GetMeQuery } from 'graphql/types';

export const DURATION_HH_MM = 'hh:mm';
export const DURATION_HH_MM_SS = 'hh:mm:ss';
export const MAX_SUPPORTED_HISTORICAL_DAYS = 90;

export const MIN_SUPPORTED_DATE = DateTime.local().startOf('day').minus({
  days: MAX_SUPPORTED_HISTORICAL_DAYS
});

export const getLuxonJsDateTimeFormatFromMe = (
  me: GetMeQuery['me']
): string => {
  const luxonPattern =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern;
  const luxonTimePattern =
    me?.locale?.dateTimeSettings?.shortTimeFormat?.luxonPattern;

  const combinedPattern = `${luxonPattern} ${luxonTimePattern}`;

  return luxonPattern && luxonTimePattern ? combinedPattern : 'MMM d, yyyy tt';
};

export const mapDateToISOString = (date?: Date | null): string | null => {
  const dateString = date && DateTime.fromJSDate(date).toISO();
  if (dateString)
    return DateTime.fromISO(dateString, { zone: 'utc' })?.toISO() || null;
  return null;
};

export const formatDateFromIsoString = (
  dateISOString: string,
  me: GetMeQuery['me']
): string => {
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortDateFormat?.luxonPattern ||
    'MMM d, yyyy';
  return dateISOString && DateTime.fromISO(dateISOString).toFormat(luxonFormat);
};

export const formatTimeFromIsoString = (
  dateISOString: string,
  me: GetMeQuery['me']
): string => {
  const luxonFormat =
    me?.locale?.dateTimeSettings?.shortTimeFormat?.luxonPattern ||
    // eslint-disable-next-line quotes
    "h':'mm a";
  return dateISOString && DateTime.fromISO(dateISOString).toFormat(luxonFormat);
};

export const isWeekend = (date?: Date): boolean => {
  const day = date?.getDay();
  return day === 0 || day === 6;
};

export const getDateTimeFromIso = (
  date?: DateTime | null
): DateTime | null | undefined => {
  if (date) {
    return DateTime.fromISO(date.toISODate());
  }
  return date;
};

export const getDateTimeFromISO = (date?: string): DateTime | undefined => {
  if (date) {
    return DateTime.fromISO(date);
  }
  return;
};

export const getDateTimeFromJsDate = (date: Date): DateTime => {
  return DateTime.fromObject({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear()
  });
};

export const getJsDateFromLuxonDateTime = (
  me: GetMeQuery['me'],
  date: DateTime
): string => {
  const locale = me?.locale?.language?.languageCode || 'en-US';
  const ianaName = me?.locale?.timeZone?.ianaName;
  return new Date(date.toISO()).toLocaleString(locale, {
    timeZone: ianaName
  });
};

export const getFullDateTimeFromJsDate = (date: Date): DateTime => {
  return DateTime.fromObject({
    day: date.getDate(),
    month: date.getMonth() + 1,
    year: date.getFullYear(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds()
  });
};

export const getFullDateTimeFromJsDateInZone = (
  date: Date,
  ianaName?: string
): DateTime => {
  return DateTime.fromObject(
    {
      day: date.getDate(),
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      hour: date.getHours(),
      minute: date.getMinutes(),
      second: date.getSeconds()
    },
    { zone: ianaName ?? 'utc' }
  );
};

export const getStartOfMonth = (): DateTime => DateTime.now().startOf('month');
export const getEndOFMonth = (): DateTime => DateTime.now().endOf('month');
